import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { demoBrands, demoProducts, demoSessions, demoWorthProducts } from '../data/demo'
import type { BrandSection, FlashSession, Product } from '../types'

interface AppState {
  brands: BrandSection[]
  sessions: FlashSession[]
  products: Product[]
  worthProducts: Product[]
  authenticated: boolean
  demoMode: boolean
  apiOnline: boolean
  loading: boolean
  clickBehavior: 'none' | 'open'
  refresh: () => Promise<void>
  login: (password: string) => Promise<void>
  logout: () => Promise<void>
  toggleDemoMode: (value?: boolean) => void
  saveProduct: (product: Product) => Promise<void>
  duplicateProduct: (product: Product) => Promise<void>
  deleteProduct: (product: Product) => Promise<void>
}

const AppContext = createContext<AppState | null>(null)

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [brands, setBrands] = useState(demoBrands)
  const [sessions, setSessions] = useState(demoSessions)
  const [products, setProducts] = useState(demoProducts)
  const [worthProducts, setWorthProducts] = useState(demoWorthProducts)
  const [authenticated, setAuthenticated] = useState(false)
  const [apiOnline, setApiOnline] = useState(false)
  const [loading, setLoading] = useState(true)
  const [clickBehavior, setClickBehavior] = useState<'none'|'open'>('none')
  const [demoMode, setDemoMode] = useState(() => localStorage.getItem('showcase-demo-mode') === '1')

  const refresh = useCallback(async () => {
    try {
      const [nextBrands, nextSessions, nextProducts, nextWorthProducts, auth, settings] = await Promise.all([api.brands(), api.sessions(), api.products(), api.worthProducts(), api.session(), api.settings()])
      setBrands(nextBrands); setSessions(nextSessions); setProducts(nextProducts)
      setWorthProducts(nextWorthProducts)
      setClickBehavior(settings.product_click_behavior === 'open' ? 'open' : 'none')
      setAuthenticated(auth.authenticated); setApiOnline(true)
    } catch {
      setApiOnline(false)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void refresh() }, [refresh])
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && demoMode) setDemoMode(false)
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'e') setDemoMode((value) => !value)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [demoMode])
  useEffect(() => { localStorage.setItem('showcase-demo-mode', demoMode ? '1' : '0') }, [demoMode])
  useEffect(() => {
    const controller = new AbortController()
    const context = document.modelContext
    if (!context?.registerTool) return
    void Promise.resolve(context.registerTool({ name:'set_demo_mode', title:'切换演示模式', description:'打开或关闭精选橱窗的演示模式。', inputSchema:{type:'object',properties:{enabled:{type:'boolean'}},required:['enabled'],additionalProperties:false},annotations:{readOnlyHint:false,untrustedContentHint:false}, execute:(input:{enabled:boolean})=>{setDemoMode(Boolean(input.enabled));return{enabled:Boolean(input.enabled)}} },{signal:controller.signal})).catch(()=>undefined)
    return () => controller.abort()
  }, [])

  const replaceEverywhere = (next: Product) => {
    setProducts((items) => items.map((item) => item.id === next.id ? next : item).sort((a,b)=>a.sortOrder-b.sortOrder))
    setBrands((items) => items.map((section) => ({ ...section, products: section.products.map((item) => item.id === next.id ? next : item).sort((a,b)=>a.sortOrder-b.sortOrder) })))
    setSessions((items) => items.map((session) => ({ ...session, products: session.products.map((item) => item.id === next.id ? next : item).sort((a,b)=>a.sortOrder-b.sortOrder) })))
    setWorthProducts((items) => items.map((item) => item.id === next.id ? next : item).sort((a,b)=>a.sortOrder-b.sortOrder))
  }

  const value = useMemo<AppState>(() => ({
    brands, sessions, products, worthProducts, authenticated, demoMode, apiOnline, loading, clickBehavior, refresh,
    login: async (password) => { await api.login(password); setAuthenticated(true); await refresh() },
    logout: async () => { await api.logout(); setAuthenticated(false) },
    toggleDemoMode: (value) => setDemoMode((current) => value ?? !current),
    saveProduct: async (product) => {
      const next = apiOnline ? await api.saveProduct(product) : product
      replaceEverywhere(next)
      localStorage.setItem('showcase-last-product', String(next.id))
    },
    duplicateProduct: async (product) => {
      if (!apiOnline) throw new Error('请使用完整本地服务或部署版本复制商品')
      await api.duplicateProduct(product.id); await refresh()
    },
    deleteProduct: async (product) => {
      if (!apiOnline) throw new Error('请使用完整本地服务或部署版本删除商品')
      await api.deleteProduct(product.id); await refresh()
    },
  }), [brands, sessions, products, worthProducts, authenticated, demoMode, apiOnline, loading, clickBehavior, refresh])

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export function useApp() {
  const value = useContext(AppContext)
  if (!value) throw new Error('AppContext missing')
  return value
}
